import { ApolloError, DocumentNode } from '@apollo/client';
import { ListType } from 'constants/listTypes';
import { SORT_DIRECTIONS } from 'constants/sortDirectionsEnum';
import { GRAPHQL_TYPENAMES, GraphQLTypeNameData } from 'modules/graphql';
import { useMemo } from 'react';
import { useGraphql } from 'utils/hooks/useGraphql';
import { queryLists } from '../queries';

export type GraphQLAudienceList = {
  id: number;
  name: string;
  listType: ListType;
  creatorUserId: string;
  createdAt: string;
  updatedAt: string;
  description: string;
  hasUnsupportedEmbeddedEventType?: boolean;
  hasUnsupportedEmbeddedListReference?: boolean;
} & GraphQLTypeNameData<GRAPHQL_TYPENAMES.LISTS>;

export type QueryAudienceListsInfo = {
  count: number;
  offset: number;
  limit: number;
  page: number;
} & GraphQLTypeNameData<GRAPHQL_TYPENAMES.INFO>;

export type QueryAudienceListsResponse = {
  lists: {
    info: QueryAudienceListsInfo;
    lists: GraphQLAudienceList[];
  };
};

export type QueryAudienceListsSort = {
  sortBy: string;
  sortDirection: SORT_DIRECTIONS;
};

export type QueryAudienceListsVariables = {
  search?: string | null;
  sort?: {
    sortBy: string;
    sortDirection: SORT_DIRECTIONS;
  };
  pagination?: {
    limit: number;
    offset: number;
  };
  filterInfo?: {
    listType?: string | string[] | null;
  };
};

export type UseQueryAudienceListsState = {
  errorMessage?: ApolloError;
  hasError: boolean;
  isLoading: boolean;
  refetch: (variables: Partial<QueryAudienceListsVariables>) => void;
  info: QueryAudienceListsInfo;
  results: GraphQLAudienceList[];
};

export const useQueryAudienceLists = (
  variables: QueryAudienceListsVariables = {},
  query: DocumentNode = queryLists,
): UseQueryAudienceListsState => {
  const queryOptions = useMemo(
    () => ({ notifyOnNetworkStatusChange: true, variables: { ...variables } }),
    [variables],
  );

  const {
    data,
    errorMessage,
    isLoading,
    error: hasError = false,
    refetch,
  } = useGraphql(query, queryOptions);

  const { results = [], info = {} } = data?.lists || {};

  return { info, results, errorMessage, hasError, isLoading, refetch };
};
